import { Timestamp } from 'firebase/firestore';

import dayjs, { Dayjs } from 'dayjs';

import { CoursePair } from '../types/Course';
import { ParticipantOrder } from '../types/Event';
import League from '../types/League';
import Org, { TimeOfDay } from '../types/Org';
import Season from '../types/Season';

import { ToDayJS } from './utils';

/**
 * Effective defaults for event creation, merged from league and org.
 * League values take precedence over org values.
 */
export interface EffectiveDefaults {
  signupOpensDaysPrior: number;
  signupClosesDaysPrior: number;
  coursePair: CoursePair | undefined;
  ghinIndexSetDaysPrior: number;
  signupPriority: ParticipantOrder | undefined;
  startTime: TimeOfDay;
  maxPlayers: number;
  teeSheetGroupSize: number;
  showRosterToAllDaysPrior: number | undefined;
  showRosterToTeamDaysPrior: number | undefined;
}

/**
 * System defaults used when neither league nor org has a value set.
 */
const SYSTEM_DEFAULTS: EffectiveDefaults = {
  signupOpensDaysPrior: 6,
  signupClosesDaysPrior: 2,
  coursePair: undefined,
  ghinIndexSetDaysPrior: 0,
  signupPriority: undefined,
  startTime: { hour: 9, minute: 0 },
  maxPlayers: 24,
  teeSheetGroupSize: 4,
  showRosterToAllDaysPrior: undefined,
  showRosterToTeamDaysPrior: undefined,
};

/**
 * Merge league and org defaults. League takes precedence over org.
 * Falls back to system defaults if neither has a value.
 */
export function getEffectiveDefaults(league?: League, org?: Org): EffectiveDefaults {
  return {
    signupOpensDaysPrior:
      league?.defaultEventSignupOpensDaysPrior ??
      org?.defaultEventSignupOpensDaysPrior ??
      SYSTEM_DEFAULTS.signupOpensDaysPrior,
    signupClosesDaysPrior:
      league?.defaultEventSignupClosesDaysPrior ??
      org?.defaultEventSignupClosesDaysPrior ??
      SYSTEM_DEFAULTS.signupClosesDaysPrior,
    coursePair:
      league?.defaultCoursePair ?? org?.defaultCoursePair ?? SYSTEM_DEFAULTS.coursePair,
    ghinIndexSetDaysPrior:
      league?.defaultGhinIndexSetDaysPrior ??
      org?.defaultGhinIndexSetDaysPrior ??
      SYSTEM_DEFAULTS.ghinIndexSetDaysPrior,
    signupPriority:
      league?.defaultSignupPriority ?? org?.defaultSignupPriority ?? SYSTEM_DEFAULTS.signupPriority,
    startTime:
      league?.defaultStartTime ?? org?.defaultStartTime ?? SYSTEM_DEFAULTS.startTime,
    maxPlayers:
      league?.defaultMaxPlayers ?? org?.defaultMaxPlayers ?? SYSTEM_DEFAULTS.maxPlayers,
    teeSheetGroupSize:
      league?.defaultTeeSheetGroupSize ?? org?.defaultTeeSheetGroupSize ?? SYSTEM_DEFAULTS.teeSheetGroupSize,
    showRosterToAllDaysPrior:
      league?.defaultShowRosterToAllDaysPrior ?? org?.defaultShowRosterToAllDaysPrior ?? SYSTEM_DEFAULTS.showRosterToAllDaysPrior,
    showRosterToTeamDaysPrior:
      league?.defaultShowRosterToTeamDaysPrior ?? org?.defaultShowRosterToTeamDaysPrior ?? SYSTEM_DEFAULTS.showRosterToTeamDaysPrior,
  };
}

/**
 * Calculate the date the roster becomes visible, based on event date and days prior.
 * Returns undefined if daysPrior is undefined (roster always visible).
 */
export function calculateShowRosterDate(eventDate: Dayjs, daysPrior: number | undefined): Timestamp | undefined {
  if (daysPrior === undefined) return undefined;
  return Timestamp.fromDate(eventDate.subtract(daysPrior, 'day').startOf('day').toDate());
}

/**
 * Calculate signup opens timestamp based on event date and days prior.
 * Opens at midnight on the calculated date.
 */
export function calculateSignupOpens(eventDate: Dayjs, daysPrior: number): Timestamp {
  const opensDate = eventDate.subtract(daysPrior, 'day').startOf('day');
  return Timestamp.fromDate(opensDate.toDate());
}

/**
 * Calculate signup closes timestamp based on event date and days prior.
 * Closes at end of day (23:59:59) on the calculated date.
 */
export function calculateSignupCloses(eventDate: Dayjs, daysPrior: number): Timestamp {
  const closesDate = eventDate.subtract(daysPrior, 'day').endOf('day');
  return Timestamp.fromDate(closesDate.toDate());
}

/**
 * Calculate GHIN index date based on event date and days prior.
 * Returns the date that GHIN indexes should be pulled from.
 */
export function calculateGhinIndexDate(eventDate: Dayjs, daysPrior: number): Timestamp {
  const ghinDate = eventDate.subtract(daysPrior, 'day').startOf('day');
  return Timestamp.fromDate(ghinDate.toDate());
}

/**
 * Calculate the next event date based on latest event date and default play days.
 * Returns the next occurrence of a play day after the latest event.
 * If no latest event, returns next occurrence from today.
 * If no play days configured, returns next week from the reference date.
 */
export function calculateNextEventDate(
  latestEventDate?: Timestamp,
  defaultPlayDays?: number[]
): Dayjs {
  // Start from the day after latest event, or today if no previous event
  const referenceDate = latestEventDate
    ? ToDayJS(latestEventDate).add(1, 'day')
    : dayjs().add(1, 'day');

  // If no play days configured, return one week from reference
  if (!defaultPlayDays || defaultPlayDays.length === 0) {
    return referenceDate.add(6, 'day');
  }

  // Sort play days for consistent iteration
  const sortedDays = [...defaultPlayDays].sort((a, b) => a - b);

  // Find the next play day
  let candidate = referenceDate;
  for (let i = 0; i < 7; i++) {
    const dayOfWeek = candidate.day();
    if (sortedDays.includes(dayOfWeek)) {
      return candidate;
    }
    candidate = candidate.add(1, 'day');
  }

  // Fallback (shouldn't reach here if defaultPlayDays is valid)
  return referenceDate.add(7, 'day');
}

/**
 * Find the season that encompasses the given event date.
 * First looks for a season where the date falls within its range.
 * If no match, falls back to the catch-all season (if one exists).
 * Returns undefined if no matching season or catch-all is found.
 */
export function findSeasonForDate(eventDate: Dayjs, seasons?: Season[]): Season | undefined {
  if (!seasons || seasons.length === 0) {
    return undefined;
  }

  // Separate real seasons from catch-all
  const realSeasons = seasons.filter((s) => !s.isCatchAll);
  const catchAllSeason = seasons.find((s) => s.isCatchAll);

  // First, try to find a season where the date falls within range
  for (const season of realSeasons) {
    const startTime = ToDayJS(season.startTime as Timestamp);
    const endTime = ToDayJS(season.endTime as Timestamp);

    // Check if event date falls within season range (inclusive)
    if (
      (eventDate.isAfter(startTime) || eventDate.isSame(startTime, 'day')) &&
      (eventDate.isBefore(endTime) || eventDate.isSame(endTime, 'day'))
    ) {
      return season;
    }
  }

  // Fall back to catch-all season if no date match found
  return catchAllSeason;
}

/**
 * Apply a time of day to a date, returning a new Dayjs instance.
 */
export function applyTimeToDate(date: Dayjs, time: TimeOfDay): Dayjs {
  return date.hour(time.hour).minute(time.minute).second(0).millisecond(0);
}

/**
 * Convert a Dayjs time to TimeOfDay interface.
 */
export function dayjsToTimeOfDay(date: Dayjs): TimeOfDay {
  return {
    hour: date.hour(),
    minute: date.minute(),
  };
}

/**
 * Check if an effective default differs from org default (i.e., set at league level).
 */
export function isLeagueOverride(
  field: keyof EffectiveDefaults,
  league?: League,
  _org?: Org
): boolean {
  if (!league) return false;

  switch (field) {
    case 'signupOpensDaysPrior':
      return league.defaultEventSignupOpensDaysPrior !== undefined;
    case 'signupClosesDaysPrior':
      return league.defaultEventSignupClosesDaysPrior !== undefined;
    case 'coursePair':
      return league.defaultCoursePair !== undefined;
    case 'ghinIndexSetDaysPrior':
      return league.defaultGhinIndexSetDaysPrior !== undefined;
    case 'signupPriority':
      return league.defaultSignupPriority !== undefined;
    case 'startTime':
      return league.defaultStartTime !== undefined;
    case 'maxPlayers':
      return league.defaultMaxPlayers !== undefined;
    case 'showRosterToAllDaysPrior':
      return league.defaultShowRosterToAllDaysPrior !== undefined;
    case 'showRosterToTeamDaysPrior':
      return league.defaultShowRosterToTeamDaysPrior !== undefined;
    default:
      return false;
  }
}
